import type { UndefinedOnPartialDeep } from 'type-fest';

import { useCoworkerProfileStore } from '@/store/coworker-preferences.store.ts';
import { useTimeWindowUpdateStore } from '@/store/time-window-update.store';
import InlineMessage from '@ingka/inline-message';
import Skeleton from '@ingka/skeleton';
import informationCircleIcon from '@ingka/ssr-icon/paths/information-circle';
import warningTriangleIcon from '@ingka/ssr-icon/paths/warning-triangle';
import { translate } from '@repo/phrase';
import type { Service } from '@repo/types/service';
import { DayPicker } from '@repo/ui/daypicker';
import { TimeWindowsList } from '@repo/ui/time-windows-list';

import { getOrderModificationReasons } from '@/utils/asset.utils.ts';

import { ModifyReasonSelect } from './modify-reason-select.component';
import { useTimeWindowSelection } from './time-window-selection.hooks';

import styles from './styles.module.scss';

const TIME_WINDOW_SKELETON_ROWS = ['time-window-skeleton-1', 'time-window-skeleton-2', 'time-window-skeleton-3'];

interface Props {
    service: UndefinedOnPartialDeep<Service>;
}

export const TimeWindowSelection = ({ service }: Props) => {
    const {
        availableDates,
        currentDate,
        dateFormat,
        handleDayChange,
        handleMonthChange,
        handleTimeWindowSelect,
        hasOrderData,
        hasServicePriceError,
        initialDate,
        isError,
        isFetching,
        selectedDate,
        selectedTimeWindowId,
        shouldShowEmptyMessage,
        shouldShowTimeWindowList,
        startMonth,
        timeWindowItems,
        timeFormat,
        timeZone,
        weekStartsOn,
    } = useTimeWindowSelection(service);

    const hasSubmitError = useTimeWindowUpdateStore((state) => state.hasSubmitError);

    const { storePreferencesConfig } = useCoworkerProfileStore((state) => state);
    const orderModificationChangeReasonCodesKey = service.isDelivery
        ? 'REASON_CODES_CHANGE_DEL_DATE'
        : 'REASON_CODES_CHANGE_SERVICE_PRODUCT_DATE';

    const orderModificationChangeReasonCodes = getOrderModificationReasons(
        orderModificationChangeReasonCodesKey,
        storePreferencesConfig?.preferences
    );

    if (!hasOrderData) {
        return (
            <div className={styles.time_windows_container} data-testid="article-time-windows-view">
                <InlineMessage
                    variant="negative"
                    ssrIcon={warningTriangleIcon}
                    title={translate('time_windows_error_title')}
                    body={translate('time_windows_error_invalid_asset')}
                />
            </div>
        );
    }
    return (
        <div className={styles.time_windows_container} data-testid="article-time-windows-view">
            <DayPicker
                translate={translate}
                weekNumbers={true}
                onDayChange={handleDayChange}
                onMonthChange={handleMonthChange}
                availableDates={availableDates}
                startMonth={startMonth}
                initialDate={initialDate}
                weekStartsOn={weekStartsOn}
                currentDate={currentDate}
                timeZone={timeZone ?? 'UTC'}
            />
            {shouldShowTimeWindowList && selectedDate && (
                <>
                    <TimeWindowsList
                        items={timeWindowItems}
                        selectedDate={selectedDate}
                        selectedTimeWindowId={selectedTimeWindowId}
                        dateFormat={dateFormat}
                        timeFormat={timeFormat}
                        onTimeWindowSelect={handleTimeWindowSelect}
                        timeZone={timeZone}
                    />
                    <ModifyReasonSelect reasonCodes={orderModificationChangeReasonCodes} />
                    {hasServicePriceError && (
                        <InlineMessage
                            variant="cautionary"
                            ssrIcon={warningTriangleIcon}
                            body={translate('time_windows_error_service_price')}
                            data-testid="time-windows-service-price-error"
                        />
                    )}
                </>
            )}
            {isFetching && (
                <div className={styles.time_windows_skeleton} aria-busy="true" aria-label={translate('loading_text')}>
                    {TIME_WINDOW_SKELETON_ROWS.map((row) => (
                        <div key={row} className={styles.time_windows_skeleton_row}>
                            <Skeleton width="10rem" />
                            <Skeleton width="5rem" />
                        </div>
                    ))}
                </div>
            )}
            {shouldShowEmptyMessage && (
                <InlineMessage
                    body={translate('time_windows_empty_message')}
                    ssrIcon={informationCircleIcon}
                    variant="informative"
                    data-testid="time-windows-empty-message"
                />
            )}
            {isError && (
                <InlineMessage
                    variant="negative"
                    ssrIcon={warningTriangleIcon}
                    title={translate('time_windows_error_title')}
                    body={translate('time_windows_error_loading')}
                    data-testid="time-windows-error-message"
                />
            )}
            {hasSubmitError && (
                <InlineMessage
                    variant="negative"
                    ssrIcon={warningTriangleIcon}
                    body={translate('time_windows_error_update')}
                />
            )}
        </div>
    );
};
